import React, { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { CiSearch } from "react-icons/ci";
import { useNavigate } from "react-router-dom";
import "./suggestedServices.css";
import { REST_API_URL } from "../../api/config";
import { buildHeaders } from "../../api/buildHeaders";
import { smartDuration } from "../../utils/duration/smartDuration.ts";

const API_ROOT = REST_API_URL;

const getUserTimeZone = () => {
  if (typeof window === "undefined") return "UTC";
  return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
};

export default function SuggestedServices() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isFetched = useRef(false);

  const [services, setServices] = useState([]);
  const [selected, setSelected] = useState([]);
  const [currency, setCurrency] = useState("USD");
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);
  const [error, setError] = useState("");
  const [userTimeZone, setUserTimeZone] = useState("UTC");

  const selectedNiche =
    JSON.parse(localStorage.getItem("selectedNiche") || "null") || {};
  const selectedSubNiches =
    JSON.parse(localStorage.getItem("selectedSubNiches") || "[]") || [];

  const selectedSubNicheIds = Array.isArray(selectedSubNiches)
    ? selectedSubNiches.map((v) => Number(v)).filter((v) => !isNaN(v))
    : [];

  useEffect(() => {
    const timeZone = getUserTimeZone();
    const userStatus = localStorage.getItem("user_status") || "individual";
    const currentCode = userStatus === "salon" ? "228" : "216";

    setUserTimeZone(timeZone);
    localStorage.setItem("onboarding_code", currentCode);

    sessionStorage.setItem("onboarding_force_route", "/suggested-services");
    sessionStorage.setItem("onboarding_force_status", userStatus);
  }, []);

  const getValidToken = async () => {
    let token =
      localStorage.getItem("accessToken") ||
      sessionStorage.getItem("accessToken") ||
      "";

    if (!token) return null;

    const headers = buildHeaders();

    const verifyRes = await fetch(`${API_ROOT}/auth/token/verify/`, {
      method: "POST",
      headers,
      body: JSON.stringify({ token }),
    });

    if (verifyRes.ok) return token;

    const refresh =
      localStorage.getItem("refreshToken") ||
      sessionStorage.getItem("refreshToken");

    if (!refresh) return null;

    const refreshRes = await fetch(`${API_ROOT}/auth/token/refresh/`, {
      method: "POST",
      headers,
      body: JSON.stringify({ refresh }),
    });

    const data = await refreshRes.json().catch(() => ({}));

    if (refreshRes.ok && data.access) {
      const useLocalStorage =
        !!localStorage.getItem("accessToken") ||
        !!localStorage.getItem("refreshToken");

      if (useLocalStorage) {
        localStorage.setItem("accessToken", data.access);
      } else {
        sessionStorage.setItem("accessToken", data.access);
      }

      return data.access;
    }

    return null;
  };

  useEffect(() => {
    const handleBeforeUnload = () => {
      localStorage.setItem("suggested_services_closed", "true");
      localStorage.setItem("suggested_services_timezone", userTimeZone);
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    const wasClosed = localStorage.getItem("suggested_services_closed");
    const userStatus = localStorage.getItem("user_status") || "individual";
    const chooseNicheCode = userStatus === "salon" ? "226" : "214";

    if (wasClosed === "true") {
      localStorage.removeItem("suggested_services_closed");
      localStorage.setItem("onboarding_code", chooseNicheCode);

      sessionStorage.setItem("onboarding_force_route", "/choose-niche");
      sessionStorage.setItem("onboarding_force_status", userStatus);

      navigate("/choose-niche", { replace: true });
      return () => {
        window.removeEventListener("beforeunload", handleBeforeUnload);
      };
    }

    localStorage.setItem(
      "suggested_services_access_time",
      new Date().toISOString(),
    );

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [navigate, userTimeZone]);

  useEffect(() => {
    const fetchCurrency = async () => {
      try {
        const token = await getValidToken();
        if (!token) return;

        const headers = buildHeaders();

        const res = await fetch(`${API_ROOT}/service/user-currency/`, {
          headers,
        });

        if (res.ok) {
          const data = await res.json().catch(() => ({}));
          if (data?.currency) {
            setCurrency(String(data.currency));
          }
        }
      } catch {}
    };

    fetchCurrency();
  }, []);

  useEffect(() => {
    if (isFetched.current || !selectedNiche?.id) {
      if (!selectedNiche?.id) {
        setError(t("auth.errorNoNiche"));
        setPageLoading(false);
      }
      return;
    }

    isFetched.current = true;

    const fetchServices = async () => {
      try {
        setPageLoading(true);
        setError("");

        const token = await getValidToken();
        if (!token) {
          setError(t("auth.errorSessionExpired"));
          setPageLoading(false);
          return;
        }

        const headers = buildHeaders();

        const res = await fetch(`${API_ROOT}/auth/profession-categories/`, {
          method: "GET",
          headers,
        });

        if (!res.ok) throw new Error(t("auth.errorFetchCategories"));

        const data = await res.json().catch(() => []);

        const niche = Array.isArray(data)
          ? data.find(
              (item) =>
                Number(item.id) === Number(selectedNiche.id) ||
                item.title === selectedNiche.title ||
                item.title === selectedNiche.name,
            )
          : null;

        if (!niche) {
          setServices([]);
          return;
        }

        let professions = [];

        try {
          professions =
            typeof niche.profesions_list === "string"
              ? JSON.parse(niche.profesions_list)
              : niche.profesions_list || [];
        } catch {
          professions = [];
        }

        const selectedProfessions = professions.filter((p) =>
          selectedSubNicheIds.includes(Number(p.id)),
        );

        const normalized = selectedProfessions.flatMap((p) =>
          Array.isArray(p.suggested_service_list)
            ? p.suggested_service_list.map((s) => ({
                id: Number(s.id),
                title: s.title || s.name || "Untitled",
                description: s.description || "",
                duration: Number(s.duration) || 30,
                price: Number(s.price) || 0,
                time_unit: s.time_unit || "minutes",
                currency: s.currency || "",
                image_url: s.image_url || s.image || null,
              }))
            : [],
        );

        setServices(normalized);
        localStorage.setItem(
          "suggested_services_load_time",
          new Date().toISOString(),
        );
      } catch (err) {
        console.error("Failed to load suggested services:", err);
        setError(t("auth.errorFetchServices"));
      } finally {
        setPageLoading(false);
      }
    };

    fetchServices();
  }, [selectedNiche?.id]);

  const toggleSelect = (id) => {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );

    localStorage.setItem("service_selection_time", new Date().toISOString());
  };

  const handleNext = async () => {
    const token = await getValidToken();
    if (!token) {
      setError(t("auth.errorAuthRequired"));
      return;
    }

    const userStatus = localStorage.getItem("user_status") || "individual";
    const nextCode = userStatus === "salon" ? "228" : "216";

    if (selected.length > 0) {
      const payload = selected
        .map((id) => services.find((x) => x.id === id))
        .filter(Boolean)
        .map((s) => ({
          id: s.id,
          name: s.title,
          description: s.description,
          duration: s.duration,
          price: String(s.price),
          service_count: "1",
          time_unit: s.time_unit || "minutes",
          currency: s.currency || currency,
          image_url: s.image_url,
          isFromSuggested: true,
        }));

      localStorage.setItem("selectedServicesPayload", JSON.stringify(payload));
      localStorage.setItem("selected_services_time", new Date().toISOString());
      localStorage.setItem("selected_services_timezone", userTimeZone);
    }

    localStorage.setItem("onboarding_code", nextCode);
    localStorage.setItem(
      "suggested_services_completion_time",
      new Date().toISOString(),
    );

    sessionStorage.setItem("onboarding_force_route", "/my-services");
    sessionStorage.setItem("onboarding_force_status", userStatus);

    navigate("/my-services", {
      replace: true,
      state: {
        timeZone: userTimeZone,
        hasSelectedServices: selected.length > 0,
      },
    });
  };

  useEffect(() => {
    const handleGlobalKeyDown = (e) => {
      if (e.key === "Enter" && !loading && !pageLoading) {
        e.preventDefault();
        handleNext();
      }
    };

    window.addEventListener("keydown", handleGlobalKeyDown);
    return () => window.removeEventListener("keydown", handleGlobalKeyDown);
  }, [loading, pageLoading, selected, services, userTimeZone]);

  const filtered = services.filter((s) =>
    s.title.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  return (
    <motion.div
      className="suggested-services"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
    >
      <main className="suggested-services__container-card">
        <div className="header-row header-row-suggested">
          <h2>{t("auth.suggestedTitle")}</h2>
          <p>{t("auth.suggestedSubtitle")}</p>
        </div>

        <div className="search-bar-parent">
          <div className="search-bar">
            <input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={t("auth.searchPlaceholder")}
              autoFocus
            />
            <CiSearch size={22} />
          </div>
        </div>

        {pageLoading ? (
          <div className="loading-suggested-services">
            <div className="spinner" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="suggested-services-empty">
            <CiSearch size={40} className="empty-icon" />
            <p>{t("auth.noServicesFound")}</p>
            <span>{t("auth.noServicesFoundSubtitle")}</span>
          </div>
        ) : (
          <div className="service-grid">
            <AnimatePresence>
              {filtered.map((item) => {
                const active = selected.includes(item.id);

                return (
                  <motion.div
                    key={item.id}
                    onClick={() => toggleSelect(item.id)}
                    className={`service-card ${active ? "active" : ""}`}
                    tabIndex={0}
                  >
                    {item.image_url && (
                      <img
                        src={item.image_url}
                        className="service-thumb"
                        alt={item.title}
                      />
                    )}

                    <div className="service-info">
                      <h4>{item.title}</h4>
                      <p>{item.description}</p>
                      <div className="service-meta text-[14px] text-[#9a99ab]">
                        <span>
                          {t("auth.duration")}{" "}
                          {smartDuration(`${item.duration} ${item.time_unit || "minutes"}`)}
                        </span>{" "}
                        <span>
                          {t("auth.price", {
                            price: item.price,
                            currency: currency,
                          })}
                        </span>
                      </div>
                    </div>

                    <div className="checkbox">
                      <span className="mark" />
                    </div>
                  </motion.div>
                );
              })}
            </AnimatePresence>
          </div>
        )}

        {error && <p className="error-text">{error}</p>}

        <div className="bottom-row">
          <button
            className="primary-btn"
            onClick={handleNext}
            disabled={loading || pageLoading}
          >
            {selected.length > 0 ? t("auth.btnNext") : t("auth.btnSkip")}
          </button>
        </div>
      </main>
    </motion.div>
  );
}
